import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  formatValue,
  normalizePlan,
  resolveFields,
  resolvePath,
  resolveRowUrl,
  resolveRows,
  surface,
} from './renderPlan';

/**
 * Renders a tool result as a table, driven entirely by the backend render plan.
 *
 * Row expansion is deliberately gone. The previous version multiplied rows via a
 * cartesian product whenever a column path contained an array, so one Jira issue
 * with three links silently became three rows. Array handling is now declared by
 * the plan (`aggregate: join | count | first`) and collapses into a single cell.
 */
const DynamicTable = ({ plan: rawPlan, data, uiView }) => {
  const popupRef = useRef(null);
  const intervalRef = useRef(null);
  const [isPopupActive, setIsPopupActive] = useState(false);
  const [sort, setSort] = useState({ id: null, dir: 'asc' });

  useEffect(() => {
    const handleWindowFocus = () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
        popupRef.current = null;
        setIsPopupActive(false);
      }
    };
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (popupRef.current && !popupRef.current.closed)
        popupRef.current.close();
    };
  }, []);

  const openUrlInPopup = (url) => {
    if (!url) return;
    if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    if (intervalRef.current) clearInterval(intervalRef.current);

    const w = 1000;
    const h = 600;
    const left = window.screen.width / 2 - w / 2;
    const top = window.screen.height / 2 - h / 2;
    popupRef.current = window.open(
      url,
      '_blank',
      `width=${w},height=${h},top=${top},left=${left},resizable=yes,scrollbars=yes`,
    );
    if (popupRef.current) setIsPopupActive(true);

    intervalRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        clearInterval(intervalRef.current);
        popupRef.current = null;
        setIsPopupActive(false);
      }
    }, 500);
  };

  const plan = useMemo(() => normalizePlan(rawPlan), [rawPlan]);
  const rows = useMemo(() => resolveRows(plan, data), [plan, data]);
  const fields = useMemo(
    () => resolveFields(plan, uiView, rows),
    [plan, uiView, rows],
  );

  const sortedRows = useMemo(() => {
    if (!sort.id) return rows;
    const field = fields.find((f) => f.id === sort.id);
    if (!field) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = resolvePath(a, field.value_path);
      const bv = resolvePath(b, field.value_path);
      if (av === undefined || av === null) return 1; // empties always last
      if (bv === undefined || bv === null) return -1;
      if (field.type === 'number') return (Number(av) - Number(bv)) * dir;
      if (field.type === 'date') {
        return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, fields, sort]);

  const containerStyle = {
    width: '100%',
    maxHeight: '400px',
    overflowY: 'auto',
    overflowX: 'auto',
    marginBottom: '1rem',
    borderRadius: '0.5rem',
    border: `1px solid ${surface.border}`,
    backgroundColor: surface.bg,
    transition: 'filter 0.3s ease-in-out',
    filter: isPopupActive ? 'blur(3px)' : 'none',
    pointerEvents: isPopupActive ? 'none' : 'auto',
  };

  const thStyle = {
    padding: '0.5rem 1rem',
    backgroundColor: surface.headBg,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: surface.muted,
    fontWeight: 500,
    borderBottom: `1px solid ${surface.border}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: '0.5rem 1rem',
    borderBottom: `1px solid ${surface.rowBorder}`,
  };

  const cellContentStyle = {
    maxWidth: '250px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  if (!plan || !fields.length) return null;

  // An empty result is a real outcome, not an absence of one. The old component
  // returned null here, which made "no rows", "bad path", and "no plan" all
  // look identical — a broken plan and a genuinely empty result were
  // indistinguishable to whoever was looking at the screen.
  if (!rows.length) {
    return (
      <div
        style={{
          ...containerStyle,
          maxHeight: 'none',
          padding: '0.75rem 1rem',
          color: surface.dim,
          fontSize: '0.75rem',
        }}
      >
        No results to display.
      </div>
    );
  }

  const toggleSort = (id) =>
    setSort((prev) =>
      prev.id === id
        ? { id, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { id, dir: 'asc' },
    );

  return (
    <div style={containerStyle}>
      <table
        style={{
          width: '100%',
          minWidth: '600px',
          fontSize: '0.75rem',
          textAlign: 'left',
          color: surface.text,
          borderCollapse: 'collapse',
        }}
      >
        <thead>
          <tr>
            {fields.map((f) => (
              <th
                key={f.id}
                style={thStyle}
                scope="col"
                onClick={() => toggleSort(f.id)}
                title={`Sort by ${f.label}`}
              >
                {f.label}
                {sort.id === f.id ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, rowIndex) => {
            const url = resolveRowUrl(plan, row);
            // Prefer a stable identity from the data; fall back to the index only
            // when the row genuinely has nothing to key on.
            const rowKey = row?.id ?? row?.key ?? row?._id ?? `row-${rowIndex}`;
            return (
              <tr
                key={rowKey}
                className="hover:bg-zinc-800/50"
                style={{ cursor: url ? 'pointer' : 'default' }}
                onClick={url ? () => openUrlInPopup(url) : undefined}
              >
                {fields.map((f) => {
                  const text = formatValue(resolvePath(row, f.value_path), f);
                  return (
                    <td key={f.id} style={tdStyle}>
                      <div
                        style={{
                          ...cellContentStyle,
                          color: text === null ? surface.dim : undefined,
                        }}
                        title={text ?? undefined}
                      >
                        {text ?? '—'}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DynamicTable;
